import { useEffect, useState } from "react";
import { Clock, CalendarCheck, CalendarBlank, Plus, Trash, FloppyDisk, X } from "@phosphor-icons/react";
import { api, apiError } from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { MonthCalendar, COLORS, COLOR_MAP } from "@/components/MonthCalendar";
import { toast } from "sonner";

const WEEKDAYS = ["Senin", "Selasa", "Rabu", "Kamis", "Jumat"];
const TABS = [
  { key: "mingguan", label: "Mingguan", icon: CalendarCheck },
  { key: "bulanan", label: "Bulanan", icon: CalendarBlank },
];

export default function ManageJadwal() {
  const [weekly, setWeekly] = useState({});
  const [monthly, setMonthly] = useState([]);
  const [daily, setDaily] = useState([]);
  const [tab, setTab] = useState("mingguan");
  const [wday, setWday] = useState("Senin");
  const [saving, setSaving] = useState(false);
  const now = new Date();
  const [ym, setYm] = useState([now.getFullYear(), now.getMonth()]);
  const [dayModal, setDayModal] = useState(null); // date string
  const [form, setForm] = useState({ title: "", color: "biru" });

  useEffect(() => {
    api.get("/schedule").then((r) => {
      setDaily(r.data.daily || []);
      setWeekly(r.data.weekly || WEEKDAYS.reduce((a, d) => ({ ...a, [d]: [] }), {}));
      setMonthly((r.data.monthly || []).filter((e) => /^\d{4}-\d{2}-\d{2}$/.test(e.date || "")));
    });
  }, []);

  const persist = async (nextMonthly, nextWeekly) => {
    setSaving(true);
    try {
      await api.put("/schedule", { daily, weekly: nextWeekly ?? weekly, monthly: nextMonthly ?? monthly });
      toast.success("Jadwal disimpan");
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const wItems = weekly[wday] || [];
  const setW = (i, k, v) => setWeekly({ ...weekly, [wday]: wItems.map((it, idx) => idx === i ? { ...it, [k]: v } : it) });
  const addW = () => setWeekly({ ...weekly, [wday]: [...wItems, { start: "", end: "", activity: "" }] });
  const delW = (i) => setWeekly({ ...weekly, [wday]: wItems.filter((_, idx) => idx !== i) });

  const dayEntries = dayModal ? monthly.filter((e) => e.date === dayModal) : [];
  const addEntry = async () => {
    if (!form.title.trim()) { toast.error("Isi judul kegiatan"); return; }
    const next = [...monthly, { date: dayModal, title: form.title.trim(), color: form.color }];
    setMonthly(next); setForm({ title: "", color: "biru" });
    await persist(next);
  };
  const delEntry = async (idx) => {
    const target = dayEntries[idx];
    const next = monthly.filter((e) => !(e.date === target.date && e.title === target.title && e.color === target.color));
    setMonthly(next);
    await persist(next);
  };

  const inp = "bg-stone-50 border border-stone-200 rounded-lg py-2 px-2.5 text-sm outline-none focus:border-primary";

  return (
    <div>
      <PageHeader title="Kelola Jadwal" />
      <div className="px-5 pt-4 pb-28">
        <div className="flex gap-2 mb-4">
          {TABS.map((t) => (
            <button key={t.key} data-testid={`mj-tab-${t.key}`} onClick={() => setTab(t.key)}
              className={`flex-1 py-2.5 rounded-xl text-sm font-semibold flex items-center justify-center gap-1.5 transition-colors ${tab === t.key ? "bg-primary text-white" : "bg-white border border-stone-200 text-stone-500"}`}>
              <t.icon size={16} weight="bold" /> {t.label}
            </button>
          ))}
        </div>

        {tab === "mingguan" && (
          <div data-testid="mj-weekly">
            <div className="flex gap-2 overflow-x-auto no-scrollbar mb-3">
              {WEEKDAYS.map((d) => (
                <button key={d} data-testid={`mj-wday-${d}`} onClick={() => setWday(d)}
                  className={`shrink-0 px-3.5 py-1.5 rounded-full text-sm font-semibold ${wday === d ? "bg-primary text-white" : "bg-white border border-stone-200 text-stone-500"}`}>{d}</button>
              ))}
            </div>
            <div className="space-y-2">
              {wItems.map((it, i) => (
                <div key={i} className="bg-white rounded-2xl border border-stone-200/60 p-3 shadow-sm">
                  <div className="flex gap-2 mb-2">
                    <input data-testid={`mj-w-start-${i}`} value={it.start || ""} onChange={(e) => setW(i, "start", e.target.value)} placeholder="07.30" className={`${inp} w-20`} />
                    <input data-testid={`mj-w-end-${i}`} value={it.end || ""} onChange={(e) => setW(i, "end", e.target.value)} placeholder="08.00" className={`${inp} w-20`} />
                    <button onClick={() => delW(i)} className="ml-auto shrink-0 w-9 h-9 rounded-lg bg-red-50 text-destructive flex items-center justify-center"><Trash size={16} weight="bold" /></button>
                  </div>
                  <input data-testid={`mj-w-act-${i}`} value={it.activity || ""} onChange={(e) => setW(i, "activity", e.target.value)} placeholder="Nama kegiatan" className={`${inp} w-full`} />
                </div>
              ))}
              <button data-testid="mj-add-weekly" onClick={addW} className="w-full border-2 border-dashed border-primary/40 text-primary font-semibold py-3 rounded-2xl flex items-center justify-center gap-2"><Plus size={16} weight="bold" /> Tambah Kegiatan {wday}</button>
            </div>
            <button data-testid="mj-save" onClick={() => persist()} disabled={saving}
              className="w-full mt-4 bg-primary text-white font-semibold py-3.5 rounded-full flex items-center justify-center gap-2 active:scale-95 transition-transform disabled:opacity-60">
              <FloppyDisk size={18} weight="bold" /> {saving ? "Menyimpan..." : "Simpan Jadwal Mingguan"}
            </button>
          </div>
        )}

        {tab === "bulanan" && (
          <div data-testid="mj-monthly">
            <p className="text-xs text-stone-500 mb-3">Klik tanggal untuk menambah / melihat catatan kegiatan.</p>
            <MonthCalendar ym={ym} setYm={setYm} entries={monthly} onDayClick={(d) => { setDayModal(d); setForm({ title: "", color: "biru" }); }} />
          </div>
        )}
      </div>

      {dayModal && (
        <div className="fixed inset-0 z-[60] bg-black/40 flex items-end md:items-center justify-center" onClick={() => setDayModal(null)}>
          <div onClick={(e) => e.stopPropagation()} className="bg-white w-full md:max-w-md rounded-t-3xl md:rounded-3xl p-5 max-h-[85vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-heading font-bold text-lg text-stone-800">{new Date(dayModal).toLocaleDateString("id-ID", { weekday: "long", day: "numeric", month: "long", year: "numeric" })}</h3>
              <button onClick={() => setDayModal(null)}><X size={22} className="text-stone-400" /></button>
            </div>
            <div className="space-y-2 mb-4">
              {dayEntries.length === 0 && <p className="text-stone-400 text-sm">Belum ada catatan.</p>}
              {dayEntries.map((e, i) => (
                <div key={i} className="flex items-center gap-2 bg-stone-50 rounded-xl p-2.5">
                  <span className={`w-3 h-3 rounded-full shrink-0 ${COLOR_MAP[e.color] || "bg-primary"}`} />
                  <span className="flex-1 text-sm font-semibold text-stone-700">{e.title}</span>
                  <button data-testid={`mj-del-entry-${i}`} onClick={() => delEntry(i)} className="w-8 h-8 rounded-lg bg-red-50 text-destructive flex items-center justify-center"><Trash size={14} weight="bold" /></button>
                </div>
              ))}
            </div>
            <p className="text-xs text-stone-400 mb-1">Judul kegiatan</p>
            <input data-testid="mj-entry-title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="mis. Manasik Haji" className={`${inp} w-full mb-3`} />
            <p className="text-xs text-stone-400 mb-1.5">Warna</p>
            <div className="flex gap-2 mb-4">
              {COLORS.map(([c, lbl]) => (
                <button key={c} data-testid={`mj-color-${c}`} onClick={() => setForm({ ...form, color: c })}
                  className={`w-8 h-8 rounded-full ${COLOR_MAP[c]} ${form.color === c ? "ring-2 ring-offset-2 ring-stone-400" : ""}`} title={lbl} />
              ))}
            </div>
            <button data-testid="mj-entry-save" onClick={addEntry} className="w-full bg-primary text-white font-semibold py-3 rounded-full active:scale-95 transition-transform">Tambah Catatan</button>
          </div>
        </div>
      )}
    </div>
  );
}
